import { themeAtom } from "Infrastructure/Theme/Atom/themeAtom";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { hasPermission } from "Infrastructure/Utilities/Permissions/hasPermission";
import type { MenuItemsType } from "Shared/Components/Layout/Full/Sidebar/Types/MenuItem";
import {
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	styled,
	useTheme,
} from "@mui/material";
import { useAtom } from "jotai";
import { NavLink } from "react-router-dom";

interface ItemType {
	item: MenuItemsType;
	onClick: (event: React.MouseEvent<HTMLElement>) => void;
	level: number;
	pathDirect: string;
}

const PageResource = Resources.base.menu;

export const NavItem = ({ item, level, pathDirect, onClick }: ItemType) => {
	const [themeConfiguration] = useAtom(themeAtom);
	const { isCollapse, isSidebarHover } = themeConfiguration;
	const Icon = item?.icon;
	const theme = useTheme();
	const { t } = useResource();

	const hasPerm = hasPermission(item?.permission);

	const itemIcon =
		level > 1
			? Icon && <Icon fontSize="small" />
			: Icon && <Icon fontSize="medium" />;

	const ListItemStyled = styled(ListItemButton)(() => ({
		whiteSpace: "nowrap",
		marginBottom: "2px",
		padding: "8px 10px",
		borderRadius: `${themeConfiguration.borderRadius}px`,
		backgroundColor: level > 1 ? "transparent !important" : "inherit",
		color:
			level > 1 && pathDirect === item?.href
				? `${theme.palette.primary.main}!important`
				: theme.palette.text.secondary,
		paddingLeft: level > 2 ? `${level * 15}px` : "10px",
		"&:hover": {
			backgroundColor: theme.palette.primary.light,
			color: theme.palette.primary.main,
		},
		"&.Mui-selected": {
			color: "white",
			backgroundColor: theme.palette.primary.main,
			"&:hover": {
				backgroundColor: theme.palette.primary.main,
				color: "white",
			},
		},
	}));

	const listItemProps: {
		component: React.ElementType;
		href?: string;
		target?: string;
		to?: string;
	} = {
		component: item?.external ? "a" : NavLink,
		to: item?.href,
		href: item?.external ? item?.href : "",
		target: item?.external ? "_blank" : "",
	};

	return (
		<List component="li" disablePadding key={item?.id && item.title}>
			{hasPerm && (
				<ListItemStyled
					{...listItemProps}
					disabled={item?.disabled}
					selected={pathDirect === item?.href}
					onClick={onClick}
					disableRipple
				>
					<ListItemIcon
						sx={{
							minWidth: "36px",
							color:
								level > 1 && pathDirect === item?.href
									? theme.palette.primary.main
									: "inherit",
						}}
					>
						{itemIcon}
					</ListItemIcon>
					{(!isCollapse || isSidebarHover) && (
						<ListItemText>
							{item?.title && t(PageResource.navigation[item?.title])}
						</ListItemText>
					)}
				</ListItemStyled>
			)}
		</List>
	);
};
